import React, { useState } from "react";
import {
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  DefaultButton,
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Icon,
  ProgressIndicator,
} from "@fluentui/react";
import styles from "./ExportModal.module.css";
import { ExportFormat } from "../../api/models";
import { exportAllConversations, Conversation } from "../../api";

/**
 * Validates that a download URL originates from a trusted domain.
 * Only Azure Blob Storage SAS URLs and same-origin URLs are allowed.
 */
const isTrustedDownloadUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin === window.location.origin) return true;
    if (
      parsed.protocol === "https:" &&
      parsed.hostname.endsWith(".blob.core.windows.net")
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

interface ExportAllModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  conversations: Conversation[];
}

const FORMAT_OPTIONS: { key: ExportFormat; text: string; iconName: string; disabled?: boolean }[] = [
  { key: "pdf", text: "PDF", iconName: "PDF" },
  { key: "docx", text: "Word", iconName: "WordDocument" },
  { key: "txt", text: "Text", iconName: "TextDocument" },
];

export const ExportAllModal: React.FC<ExportAllModalProps> = ({
  isOpen,
  onDismiss,
  conversations,
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("docx");
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, failed: 0 });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    setDone(false);
    const total = conversations.length;
    setProgress({ current: 0, total, failed: 0 });

    try {
      const result = await exportAllConversations({
        conversation_ids: conversations.map(c => c.id),
        format: selectedFormat,
      });

      setProgress({ current: total, total, failed: result.error ? 1 : 0 });

      if (result.error) {
        setError(result.error);
      } else if (result.download_url) {
        if (!isTrustedDownloadUrl(result.download_url)) {
          setError("Download URL is not trusted.");
        } else {
          window.location.href = result.download_url;
        }
      } else {
        setError("Export completed but no download URL was returned.");
      }
    } catch {
      setProgress({ current: total, total, failed: 1 });
      setError("An unexpected error occurred. Please try again.");
    }

    setIsExporting(false);
    setDone(true);
  };

  const handleDismiss = () => {
    if (isExporting) return;
    setError(null);
    setIsExporting(false);
    setDone(false);
    setProgress({ current: 0, total: 0, failed: 0 });
    onDismiss();
  };

  const modalProps = {
    isBlocking: isExporting,
    overlay: {
      styles: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        },
      },
    },
    styles: {
      main: {
        selectors: {
          ["@media (min-width: 480px)"]: {
            minWidth: 400,
            maxWidth: 440,
          },
        },
      },
    },
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleDismiss}
      dialogContentProps={{
        type: DialogType.largeHeader,
        title: "Export All Conversations",
        subText: `Choose a format to export all ${conversations.length} conversation(s).`,
      }}
      modalProps={modalProps}
    >
      <Stack tokens={{ childrenGap: 16 }}>
        {/* Format Selection — only when not exporting and not done */}
        {!isExporting && !done && (
          <div className={styles.formatTiles} role="radiogroup" aria-label="Export format">
            {FORMAT_OPTIONS.map(opt => {
              const isSelected = selectedFormat === opt.key;
              const isDisabled = !!opt.disabled;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  disabled={isDisabled}
                  className={[
                    styles.formatTile,
                    isSelected ? styles.formatTileSelected : "",
                    isDisabled ? styles.formatTileDisabled : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => !isDisabled && setSelectedFormat(opt.key)}
                >
                  <span className={styles.formatTileRadio}>
                    {isSelected && <span className={styles.formatTileRadioDot} />}
                  </span>
                  <Icon
                    iconName={opt.iconName}
                    styles={{ root: { fontSize: 32, width: 32, height: 32, lineHeight: "32px" } }}
                  />
                  <span className={styles.formatTileLabel}>{opt.text}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Progress */}
        {isExporting && (
          <Stack tokens={{ childrenGap: 8 }}>
            <ProgressIndicator
              label={`Generating combined ${selectedFormat.toUpperCase()} for ${progress.total} conversation(s)...`}
              percentComplete={isExporting ? undefined : 1}
            />
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
              <Spinner size={SpinnerSize.small} />
              <Text variant="small">Please wait, do not close this dialog.</Text>
            </Stack>
          </Stack>
        )}

        {/* Success */}
        {done && !error && (
          <MessageBar messageBarType={MessageBarType.success} className={styles.resultMessage}>
            All {conversations.length} conversation(s) exported successfully.
          </MessageBar>
        )}

        {/* Error */}
        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={styles.resultMessage}>
            {error}
          </MessageBar>
        )}
      </Stack>

      <DialogFooter>
        <div className={styles.footerButtons}>
          {!isExporting && !done && (
            <>
              <PrimaryButton
                onClick={handleExport}
                text="Export All"
                iconProps={{ iconName: "Download" }}
                disabled={isExporting || conversations.length === 0}
              />
              <DefaultButton onClick={handleDismiss} text="Cancel" />
            </>
          )}
          {done && (
            <DefaultButton onClick={handleDismiss} text="Close" />
          )}
          {isExporting && (
            <DefaultButton onClick={handleDismiss} text="Close" disabled />
          )}
        </div>
      </DialogFooter>
    </Dialog>
  );
};
